import { FieldArray, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { Button, TextField, Autocomplete, Grid, FormLabel, InputAdornment, Typography, Box } from '@mui/material';
import GlobalDialogContent from '../GlobalDialogContent';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../redux/reducer/snackbarSlice';
import { ProjectAssignAgency, StoreAssignAgency } from '../../../utils/services/project/project';
import { RootState } from '../../../redux/store';
import { Add } from '@mui/icons-material';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { useState } from 'react';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { closeDialog } from '../../../redux/reducer/dialogSlice';
import { setModuleRefresh } from '../../../redux/reducer/registration/RefreshSlice';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { AmountDisplay, rupeeAmountDisplay } from '../../../utils/utils';

const AssignAgency = () => {
    const dispatch = useDispatch();
    const projectData = useSelector((state: RootState) => state.dialog.payload);
    const [agencyOptionsList, setAgencyOptionsList] = useState<any[][]>([]);
    const modeOfImplementationOptions = [
        { value: "section", label: "Section-8" },
        { value: "society-trust", label: "Society/Trust" },
        { value: "donor-foundation", label: "Donor Foundation" },
        { value: "autonomous-body", label: "Statutory Body" },
        { value: "csr-cell", label: "CSR Cell" },
    ];
    const getModeOfImplementationValue = (label: string) => {
        const match = modeOfImplementationOptions.find(opt => opt.label === label);
        return match ? match.value : null;
    };

    const formik = useFormik({
        initialValues: {
            unitsCommitted: projectData?.no_of_unit || '',
            costPerUnit: projectData?.cost || '',
            project_name: projectData?.project_title || '',
            totalCommitment: projectData?.total_implementation || '',
            formItems: [{ agency_user_id: '', allocated_amount: '', comment: '', modeOfImplementation: '', }],
        },
        validationSchema: Yup.object({
            unitsCommitted: Yup.number().min(1, 'Minimum 1 unit required').required('Required'),
            costPerUnit: Yup.number().min(0, 'Cost per unit must be positive').required('Required'),
            totalCommitment: Yup.string(),
            formItems: Yup.array().of(
                Yup.object({
                    modeOfImplementation: Yup.string().required('Required'),
                    // agency_user_id: Yup.string().when('modeOfImplementation', (mode: unknown) => {
                    //     if (mode === 'CSR Cell') {
                    //       return Yup.string().notRequired();
                    //     }
                    //     return Yup.string().required('Agency is required');
                    //   }),
                    allocated_amount: Yup.number().required('Amount is required'),
                    comment: Yup.string().required('Comment is required'),
                })
            ),
        }),
        onSubmit: async (values) => {
            if (projectData?.id) {
                const reqPayload = {
                    commitment_id: projectData.id,
                    project_id: projectData.project_id,
                    agency_allocations: values.formItems.map(item => ({
                        implementation: getModeOfImplementationValue(item.modeOfImplementation),
                        remark: item.comment,
                        agency_user_id: item.agency_user_id || 0,
                        allocated_amount: Number(item.allocated_amount) || '',
                    })),
                };
                const { code, message } = await StoreAssignAgency(reqPayload);

                if (code === 200) {
                    dispatch(closeDialog());
                    dispatch(setModuleRefresh({ moduleName: 'AssignAgency', refresh: true }));
                    dispatch(showSnackbar({ type: "success", message: message || "Status updated successfully!" }));
                } else {
                    dispatch(showSnackbar({ type: "error", message: message || "Failed to update status" }));
                }
            }
        },
    });
    const implementationModes = ['CSR Cell', 'Donor Foundation', "Statutory Body", 'Society/Trust', 'Section-8'];

    const fetchImpAgency = async (selectedMode: string, rowIndex: number) => {
        const orgType = getModeOfImplementationValue(selectedMode || "");
        const reqPayload = { search: '', org_type: orgType };
        const { code, data ,message} = await ProjectAssignAgency(reqPayload);
        if (code === 200) {
            setAgencyOptionsList(prev => {
                const updated = [...prev];
                updated[rowIndex] = data;
                return updated;
            });
        } else {
            dispatch(showSnackbar({ type: "error", message : message || "Something went wrong!" }));
            setAgencyOptionsList(prev => {
                const updated = [...prev];
                updated[rowIndex] = [];
                return updated;
            });
        }
    };

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <GlobalDialogContent
                    dialogBody={
                        <>
                            <Grid container spacing={1} >
                                <Grid item xs={12} sm={12}>
                                    <TextField
                                        label="Project Name"
                                        value={formik.values.project_name}
                                        size="small"
                                        fullWidth
                                        inputProps={{ readOnly: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="No. of Units Committed"
                                        name="unitsCommitted"
                                        size="small"
                                        value={formik.values.unitsCommitted}
                                        InputProps={{ disabled: true }}
                                        inputProps={{ maxLength: 15, style: { textAlign: 'right' } }}

                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Cost per Unit"
                                        name="costPerUnit"
                                        size="small"
                                        value={rupeeAmountDisplay(formik.values.costPerUnit)}
                                        InputProps={{ disabled: true }}
                                        inputProps={{ maxLength: 15, style: { textAlign: 'right' } }}

                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Total Commitment"
                                        name="totalCommitment"
                                        size="small"
                                        InputProps={{ disabled: true }}
                                        value={rupeeAmountDisplay(formik.values.totalCommitment)}
                                        inputProps={{ maxLength: 15, style: { textAlign: 'right' } }}

                                    />
                                </Grid>
                            </Grid>
                            <FieldArray name="formItems">
                                {({ push, remove }) => (
                                    <Box>
                                        {formik.values.formItems.map((item: any, index: any) => (
                                            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }} key={index}>
                                                <Grid item xs={12} sm={12} md={12}>
                                                    <Box
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        sx={{ backgroundColor: "#ffcccc", borderRadius: 1 }}
                                                    >
                                                        <Typography variant='h6' sx={{ p: 1 }}>{index + 1}. Implementing Agency </Typography>
                                                        <Button color="primary" disabled={formik.values.formItems.length == 1} onClick={() => remove(index)}>
                                                            <RemoveCircleOutlineIcon />
                                                        </Button>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} sm={4} md={4} >
                                                    <Autocomplete
                                                        fullWidth
                                                        options={implementationModes}
                                                        value={item.modeOfImplementation}
                                                        onChange={(e, value) => {
                                                            formik.setFieldValue(`formItems.${index}.modeOfImplementation`, value);
                                                            formik.setFieldValue(`formItems.${index}.agency_user_id`, '');
                                                            fetchImpAgency(value, index);
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                size="small"
                                                                label="Mode of Implementation *"
                                                                margin="normal"
                                                            />
                                                        )}
                                                    />

                                                </Grid>
                                                <Grid item xs={12} sm={5} md={5} sx={{ mt: 1 }}>
                                                    <Autocomplete
                                                        options={agencyOptionsList[index] || []}
                                                        getOptionLabel={(option) => option?.name || ""}
                                                        value={
                                                            (agencyOptionsList[index] || []).find(
                                                                (option) => option.id === item.agency_user_id
                                                            ) || null
                                                        }
                                                        onChange={(e, value) => {
                                                            formik.setFieldValue(`formItems.${index}.agency_user_id`, value?.id || '');
                                                        }}
                                                        disabled={item.modeOfImplementation === 'CSR Cell'}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                size="small"
                                                                label="Select Implementing Agency *"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={3} md={3} sx={{ mt: 1 }}>
                                                    <TextField
                                                        fullWidth
                                                        label="Allocation Amount (₹) *"
                                                        name={`formItems.${index}.allocated_amount`}
                                                        size="small"
                                                        value={AmountDisplay(item.allocated_amount || 0)}
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(/[^0-9]/g, "");
                                                            formik.setFieldValue(`formItems.${index}.allocated_amount`, value);
                                                        }}
                                                        inputProps={{ maxLength: 15, style: { textAlign: 'right' } }}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <CurrencyRupeeIcon sx={{ fontSize: 20, ml: 2 }} />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={12} md={12}>
                                                    <FormLabel>Remarks: <span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <TextField
                                                       name={`formItems.${index}.comment`}
                                                       value={item.comment || ""}
                                                       onChange={(e) =>
                                                           formik.setFieldValue(`formItems.${index}.comment`, e.target.value)
                                                       }
                                                       placeholder="Enter Comments"
                                                       multiline
                                                       rows={3}
                                                       size="small"
                                                       fullWidth
                                                    />
                                                </Grid>
                                            </Grid>
                                        ))}

                                        <Grid item xs={12} container  >
                                            <Typography variant="h6"></Typography>
                                            <Button
                                                startIcon={<Add />}
                                                size="small"
                                                variant="outlined"
                                                color="primary"

                                                onClick={() => push({ agency_user_id: "", allocated_amount: "", comment: "", modeOfImplementation: '', })}
                                            >
                                                Agency
                                            </Button>
                                        </Grid>
                                    </Box>
                                )}
                            </FieldArray>
                        </>
                    }
                    dialogFooter={
                        <Button type="submit" startIcon={<AssignmentIndIcon />} variant="contained" color="info">
                            Assign
                        </Button>
                    }
                />
            </form>
        </FormikProvider>
    );
};

export default AssignAgency;
